/**
 * Phones: the fight loop is landscape (D1). Where the browser allows it (Android Chrome), going
 * into a fight requests fullscreen and locks landscape, so the screen turns even with rotation
 * lock on; back in the menus it unlocks. iPhone Safari can't lock orientation, so there the HUD's
 * rotate prompt does the job (docs/architecture.md §17). Must be called from a tap/click handler.
 */
type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: 'landscape') => Promise<void>;
  unlock?: () => void;
};

export function isTouchPhone(): boolean {
  return matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 600;
}

/** Opened from the home screen (installed): no browser bars. */
export function isStandalone(): boolean {
  return matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

/**
 * A phone browser that can't go fullscreen (iPhone Safari): its address and tab bars take about a
 * third of a landscape screen, so the fight is played upright there (D20). Installed, or where
 * fullscreen works (Android), the fight stays landscape (D1).
 */
export function playsUpright(): boolean {
  if (typeof matchMedia !== 'function' || typeof document === 'undefined') return false;
  const fullscreen = document.fullscreenEnabled || (document as { webkitFullscreenEnabled?: boolean }).webkitFullscreenEnabled;
  return isTouchPhone() && !isStandalone() && !fullscreen;
}

function lockLandscape(): void {
  const o = screen.orientation as LockableOrientation | undefined;
  o?.lock?.('landscape').catch(() => {
    // Not allowed here (e.g. not fullscreen, desktop, iOS): the rotate prompt covers it.
  });
}

export function enterFightMode(): void {
  if (!isTouchPhone()) return;
  const el = document.documentElement;
  if (!document.fullscreenElement && typeof el.requestFullscreen === 'function') {
    el.requestFullscreen({ navigationUI: 'hide' }).then(lockLandscape, () => lockLandscape());
  } else {
    lockLandscape();
  }
}

export function exitFightMode(): void {
  const o = screen.orientation as LockableOrientation | undefined;
  try {
    o?.unlock?.();
  } catch {
    // ignore
  }
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
}
